import { ModalProps } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { FormikProps, useFormik } from 'formik';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import {
  ArrowLeftIcon,
  Input,
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  SAVE_LIST_NAME_MAX_CHARACTERS,
  useSnackbar,
} from 'ui-components';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { routes } from '~/core/router';
import { useCreateList } from '~/hooks/api/useSavedLists';
import { SAVED_LISTS } from '~/queries/keys';

export interface ISavedListCreateListModalProps {
  creatorIds?: string[];
  isOpen: boolean;
  showBackButton?: boolean;
  onClose: () => void;
  onError?: () => void;
  onSuccess?: () => void;
  modalProps?: ModalProps;
}
interface ListCreateFormValues {
  listName: string;
}

const FORM_ID = 'list-create-form';

export const SavedListCreateListModal: React.FC<
  ISavedListCreateListModalProps
> = ({
  creatorIds,
  isOpen,
  showBackButton = true,
  onClose,
  onError,
  onSuccess,
  modalProps,
}) => {
  const { mutateAsync } = useCreateList();
  const queryClient = useQueryClient();
  const router = useRouter();
  const snackbar = useSnackbar();

  const formik = useFormik({
    initialValues: {
      listName: '',
    },
    onSubmit: ({ listName }) => handleCreateList(listName),
  }) as FormikProps<ListCreateFormValues>;

  const handleCreateList = useCallback(
    async (name: string) => {
      await mutateAsync(
        {
          creatorIds,
          name,
          saved: true,
        },
        {
          onSuccess: (newList) => {
            onSuccess && onSuccess();
            queryClient.invalidateQueries([SAVED_LISTS]);
            formik.resetForm();
            router.push(routes.savedListDetail(newList.id));
          },
          onError: () => {
            onError && onError();
            snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
          },
        },
      );
    },
    [
      creatorIds,
      formik,
      mutateAsync,
      onError,
      onSuccess,
      queryClient,
      router,
      snackbar,
    ],
  );

  const handleListCreateModalClose = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  /**
   * Handles input change with manual character limit enforcement.
   * Needed because formik.setFieldValue doesn't respect HTML maxLength attribute.
   *
   * @param event - The input change event
   * @returns void
   */
  const handleListNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.slice(0, SAVE_LIST_NAME_MAX_CHARACTERS);
    formik.setFieldValue('listName', value);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleListCreateModalClose} {...modalProps}>
      <ModalHeaderDefault
        button={
          showBackButton
            ? {
                ariaLabel: 'Go back',
                icon: <ArrowLeftIcon boxSize={4} />,
                onClick: handleListCreateModalClose,
              }
            : undefined
        }
        title="Name your list"
      />

      <ModalBody>
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <Input
            value={formik.values.listName}
            onChange={handleListNameChange}
            onClear={() => formik.setFieldValue('listName', '')}
            name="listName"
            size="xl"
            placeholder="ie; Beauty Talent in NYC"
            maxLength={SAVE_LIST_NAME_MAX_CHARACTERS}
            isFullWidth
            info={`${
              formik.values.listName?.length ?? 0
            }/${SAVE_LIST_NAME_MAX_CHARACTERS} characters`}
          />
        </form>
      </ModalBody>

      <ModalFooterDefault
        secondary={{
          title: 'Cancel',
          onClick: handleListCreateModalClose,
        }}
        primary={{
          form: FORM_ID,
          isDisabled: !formik.values.listName.length,
          isLoading: formik.isSubmitting,
          title: 'Create',
          type: 'submit',
        }}
      />
    </Modal>
  );
};
